import React, { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material';
import { Button, TextField, Typography } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import validate from 'validate.js';
import { authAction } from 'common/actions';
import { ERROR_OCCURRED, FIX_BEFORE_CONTINUE } from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Alert } from 'components';
import { Severity } from 'components/Alert/type';
import { useCreateUser } from 'services/hooks/auth';
import { giveFocusToInputWithDelay } from 'utilities';
import { dbLongDate } from 'utilities/dateFormats';
import { FormState, SignUpFormProps } from './SignUpForm.props';
import { constraints } from './SignUpForm.validation';
import { withRouter, WithRouterProps } from '../../../../services/hooks/with-router/withRouter';
import SocialLogin from '../../../Login/components/SocialLogin';
import TermsAgreed from '../TermsAgreed/TermsAgreed';

const PREFIX = 'SignUpForm';

const classes = {
  root: `${PREFIX}-root`,
  form: `${PREFIX}-form`,
  btn: `${PREFIX}-btn`,
  title: `${PREFIX}-title`,
  textField: `${PREFIX}-textField`,
  signUpButton: `${PREFIX}-signUpButton`,
  helperText: `${PREFIX}-helperText`,
  label: `${PREFIX}-label`,
  erroredLabel: `${PREFIX}-erroredLabel`,
  underline: `${PREFIX}-underline`,
  error: `${PREFIX}-error`,
  termsAgreed: `${PREFIX}-termsAgreed`,
  termsAgreedLinks: `${PREFIX}-termsAgreedLinks`,
  text: `${PREFIX}-text`,
  focusedLabel: `${PREFIX}-focusedLabel`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {},

  [`& .${classes.form}`]: {
    width: '100%',
    maxWidth: 600,
    boxSizing: 'border-box',
    padding: theme.spacing(3),
    '& h2': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
    },
    '& .scar-textField': {
      marginBottom: theme.spacing(2),
      width: '28.563rem',
      '& input': {
        borderBottom: `2px solid ${theme.palette.grey[300]}`,
      },
    },
  },

  [`& .${classes.btn}`]: {
    marginTop: theme.spacing(1),
  },

  [`& .${classes.title}`]: {
    marginTop: theme.spacing(3),
    fontSize: '1.125rem',
  },

  [`& .${classes.textField}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.signUpButton}`]: {
    margin: theme.spacing(2, 0),
    fontSize: '0.875rem',
    width: '28.563rem',
  },

  [`& .${classes.helperText}`]: {},

  [`& .${classes.label}`]: {
    '&$erroredLabel': {
      color: theme.palette.pink,
    },
  },

  [`& .${classes.erroredLabel}`]: {},

  [`& .${classes.underline}`]: {
    '&$error:after': {
      borderBottomColor: theme.palette.pink,
    },
  },

  [`& .${classes.error}`]: {},

  [`& .${classes.termsAgreed}`]: {
    fontSize: '0.875rem',
    cursor: 'default',
  },

  [`& .${classes.termsAgreedLinks}`]: {
    marginLeft: '0.2rem',
  },

  [`& .${classes.text}`]: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.focusedLabel}`]: {},
}));

const emptyForm = {
  isValid: false,
  values: {},
  touched: {},
  errors: {},
};

const SignUpForm = ({ setIsAccountCreated }: SignUpFormProps & WithRouterProps) => {
  const schema = constraints;

  const createUser = useCreateUser();
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const [formState, setFormState] = useState<FormState>(emptyForm);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<Severity>('success');

  const firstNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (firstNameInputRef.current) {
      giveFocusToInputWithDelay(firstNameInputRef);
    }
  }, [firstNameInputRef]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();

    setFormState(prevFormState => ({
      ...prevFormState,
      values: {
        ...prevFormState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const addtermsAgreedDate = (data: any) => ({
    ...data,
    profile: {
      terms_last_agreed: dbLongDate(),
    },
  });

  const handleSignUp = async (event: FormEvent<HTMLFormElement>) => {
    setIsAlertVisible(false);
    event.preventDefault();

    if (termsAgreed && formState.isValid) {
      const newUserData = addtermsAgreedDate(formState.values);
      const [data, error] = await createUser(newUserData);

      if (error) {
        setSeverity('error');
        if (error.status === 400) {
          setMessage(FIX_BEFORE_CONTINUE);
          setFormState({
            ...formState,
            errors: JSON.parse(error.response.text),
            isValid: false,
          });
        } else {
          setMessage(ERROR_OCCURRED);
        }
        setIsAlertVisible(true);
      } else {
        setFormState(emptyForm);
        dispatch(authAction.setActivationEmail(data.email));
        setIsAccountCreated(true);
      }
    }
  };

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState(prevFormState => ({
      ...prevFormState,
      isValid: isUndefined(errors) || isEmpty(errors),
      errors: errors ?? {},
    }));
  }, [formState.values, schema]);

  const hasError = (field: keyof FormState['errors']) => formState.touched[field] && formState.errors[field];

  return (
    <Root className={classes.root}>
      <form className={classes.form} onSubmit={handleSignUp}>
        <Typography className={classes.title} variant="h2">
          Create new account
        </Typography>
        <Alert severity={severity} isAlertVisible={isAlertVisible}>
          {message}
        </Alert>
        <TextField
          className="scar-textField"
          error={!!hasError('first_name')}
          fullWidth
          helperText={hasError('first_name') ? formState.errors.first_name?.[0] : null}
          label="First name"
          name="first_name"
          onChange={handleChange}
          type="text"
          value={formState.values.first_name || ''}
          inputProps={{ 'aria-label': 'first_name' }}
          id="user-name"
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputLabelProps={{
            classes: {
              root: classes.label,
              focused: classes.focusedLabel,
              error: classes.erroredLabel,
            },
          }}
          inputRef={firstNameInputRef}
        />
        <TextField
          className="scar-textField"
          error={!!hasError('last_name')}
          fullWidth
          helperText={hasError('last_name') ? formState.errors.last_name?.[0] : null}
          label="Last name"
          name="last_name"
          onChange={handleChange}
          type="text"
          value={formState.values.last_name || ''}
          inputProps={{ 'aria-label': 'last_name' }}
          id="user-last-name"
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputLabelProps={{
            classes: {
              root: classes.label,
              focused: classes.focusedLabel,
              error: classes.erroredLabel,
            },
          }}
        />
        <TextField
          className="scar-textField"
          error={!!hasError('email')}
          fullWidth
          helperText={hasError('email') ? formState.errors.email?.[0] : null}
          label="Email address"
          name="email"
          onChange={handleChange}
          type="text"
          value={formState.values.email || ''}
          inputProps={{ 'aria-label': 'email' }}
          id="user-email"
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputLabelProps={{
            classes: {
              root: classes.label,
              focused: classes.focusedLabel,
              error: classes.erroredLabel,
            },
          }}
        />
        <TextField
          className="scar-textField"
          error={!!hasError('password')}
          fullWidth
          helperText={hasError('password') ? formState.errors.password?.[0] : null}
          label="Password"
          name="password"
          onChange={handleChange}
          type="password"
          value={formState.values.password || ''}
          inputProps={{ 'aria-label': 'password' }}
          id="user-password"
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputLabelProps={{
            classes: {
              root: classes.label,
              focused: classes.focusedLabel,
              error: classes.erroredLabel,
            },
          }}
        />
        <TextField
          className="scar-textField"
          error={!!hasError('re_password')}
          fullWidth
          helperText={hasError('re_password') ? formState.errors.re_password?.[0] : null}
          label="Confirm password"
          name="re_password"
          onChange={handleChange}
          type="password"
          value={formState.values.re_password || ''}
          inputProps={{ 'aria-label': 're_password' }}
          id="user-confirm-password"
          FormHelperTextProps={{
            className: classes.helperText,
          }}
          InputLabelProps={{
            classes: {
              root: classes.label,
              focused: classes.focusedLabel,
              error: classes.erroredLabel,
            },
          }}
        />
        <TermsAgreed setTermsAgreed={setTermsAgreed} termsAgreed={termsAgreed} />
        <Button
          className={classes.signUpButton}
          color="secondary"
          disabled={!termsAgreed || !formState.isValid}
          fullWidth
          type="submit"
          variant="contained"
          name="submit">
          Sign up now
        </Button>
        <Typography component="p" variant="h3" className={classes.text}>
          OR
        </Typography>
        <SocialLogin setAlert={setIsAlertVisible} setError={setMessage} />
      </form>
    </Root>
  );
};

export default withRouter(SignUpForm);
